import { runInAction } from 'mobx'
import rootStore from '../..'
import { setMeeting } from '../../../api/modules/api-meetings'
import { warnPt } from '../../../config'
import { responseHasError } from '../../users/actions'
import { wrapWithLoading } from '../../../config/general-actions'
import { actSaveEntityForDemo } from '../../monthly-reports/actions'
import { Meeting } from '../../../cls/modules/meetings'
import { getFullItm } from '../../../api/utils/api-item'

const meetingStore = rootStore.meetings

/**
 * @async
 * @function newMeeting
 * @description Creates a new meeting item with the specified ID and marks it as new.
 * @param {string} newId - The ID of the new meeting item.
 * @returns {Promise<void>}
 */
export const newMeeting = async (newId) => {
	warnPt(newId)

	runInAction(() => {
		const _storeItm = requireMeetingById(newId)
		_storeItm.setNewFlag()
	})
}

/**
 * @function requireMeetingById
 * @description Retrieves a meeting item by its ID from the store. If it doesn't exist, creates a new meeting item.
 * @param {string} meetingId - The ID of the meeting item to retrieve.
 * @returns {Meeting} The retrieved or newly created meeting item.
 */
export const requireMeetingById = (meetingId) => {
	warnPt(meetingId)
	let meeting = meetingStore.meetings[meetingId]
	if (meeting) {
		return meeting
	}
	runInAction(() => {
		meetingStore.meetings[meetingId] = new Meeting(meetingId)
	})
	return meetingStore.meetings[meetingId]
}

/**
 * @async
 * @function actLoadMeeting
 * @description Loads a meeting item from the server by its ID and updates the store.
 * @param {string} meetingId - The ID of the meeting item to load.
 * @returns {Promise<void>}
 */
export const actLoadMeeting = wrapWithLoading(
	async (meetingId) => {
		const _response = await getFullItm('meetings', {
			id: meetingId
		})

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				const _storeItm = requireMeetingById(meetingId)
				_storeItm.updateFromServer(_response.data || {})
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Meeting' }
)

/**
 * @async
 * @function actSaveMeeting
 * @description Saves a meeting item to the server.
 * @param {string} meetingId - The ID of the meeting item to save.
 * @param {Object} full - The full data of the meeting item to save.
 * @param {boolean} [demo=false] - Whether this is a demo operation.
 * @returns {Promise<boolean>} True if the save was successful, false otherwise.
 */
export const actSaveMeeting = wrapWithLoading(
	async (meetingId, full, demo = false) => {
		if (demo) {
			actSaveEntityForDemo(
				meetingId,
				full,
				requireMeetingById,
				rootStore.uiLstMeetings,
				false
			)

			return true
		}

		const _response = await setMeeting(meetingId, full)

		const hasError = responseHasError(_response)
		if (hasError) {
			throw _response
		}
		return true
	},
	{ loadingMessage: 'Saving Meeting' }
)
